import React, { useEffect, useState } from 'react';
import { Input } from '../../common/input.component';
import { Select } from '../../common/select.component';
import { ROLES as USER_ROLES } from '../../../consts/db-values';
import { Checkbox } from '../../common/checkbox';
import { UserService } from '../../../services/user.service';
import { AlertService } from '../../../services/alert.service';
import { ModalProps, ModalsService } from '../../../services/modals.service';
import { KeyedMutator } from 'swr';
import { useSuppliers } from '../../../api/suppliers';
import { UserProps } from './users-screen.component';
import Toggle from 'rsuite/Toggle';
import { useModalClose } from '../../../utils/useModalClose';
import classNames from 'classnames';
import { useRbac } from '../../common/Rbac';
import {
  CleanUser,
  NotificationChannel,
  NotificationSetting,
  NotificationType,
  RbacObject,
} from '../../../export-types/cleaned-types';
import NotificationTable from './notification-table';

import { formatPhoneNumber, PhoneInput } from './PhoneInput';
import Whisper from 'rsuite/Whisper';
import { Tooltip } from 'rsuite';
import { BitrixMessageUserModal } from './bitrix-message-user-modal.component';

const ROLES = Object.keys(USER_ROLES).map((key) => ({
  label: USER_ROLES[key as keyof typeof USER_ROLES],
  value: key,
}));

function generatePassword() {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  let password = '';
  for (let i = 0; i < 12; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    password += chars[randomIndex];
  }

  return password;
}

interface AddUserModalProps extends ModalProps {
  user?: UserProps;
  mutate: KeyedMutator<any>;
}

export type NotificationStates = {
  promoEmail: boolean;
  serviceEmail: boolean;
  serviceSms: boolean;
  serviceTelegram: boolean;
  serviceMax: boolean;
  orderChatEmail: boolean;
  orderChatTelegram: boolean;
  orderChatMax: boolean;
  financeTelegram: boolean;
  financeMax: boolean;
  securityTelegram: boolean;
  securityMax: boolean;
};

function getNotificationState(
  notifications: NotificationSetting[],
  type: NotificationType,
  channel: NotificationChannel,
): boolean {
  const item = notifications.find((n) => n.type === type);
  if (!item) return true;
  return item[channel];
}

export const AddUserModal = ({ user, close, mutate }: AddUserModalProps) => {
  const { data: suppliers } = useSuppliers();
  const isEdit = !!user;
  const [bitrixId, setBitrixId] = useState(user?.bitrixId || '');

  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [email, setEmail] = useState(user?.email || '');
  const [role, setRole] = useState(user?.role || '');
  const [autoSend, setAutoSend] = useState(user?.autoSend || false);
  const [moderate, setModerate] = useState(user?.moderate || false);
  const [isBitrix, setIsBitrix] = useState(!!user?.bitrixId);
  const [moderateDeliveryOrder, setModerateDeliveryOrder] = useState(
    user?.moderateDeliveryOrder || false,
  );
  const [payBonusesForPurchaseOrder, setPayBonusesForPurchaseOrder] = useState(
    user?.payBonusesForPurchaseOrder ?? false,
  );

  const [paymentMethodsModerationEnabled, setPaymentMethodsModerationEnabled] =
    useState(user?.paymentMethodsModerationEnabled ?? true);

  const [password, setPassword] = useState('');
  const [purchase, setPurchase] = useState(
    user?.accesses?.includes('purchase') || false,
  );
  const [warehouse, setWarehouse] = useState(
    user?.accesses?.includes('warehouse') || false,
  );
  const [salesManager, setSalesManager] = useState(
    user?.accesses?.includes('salesManager') || false,
  );
  const [supplierFinances, setSupplierFinances] = useState(
    user?.accesses?.includes('supplierFinances') || false,
  );
  const [disableEmails, setDisableEmails] = useState(
    user?.disableEmails || false,
  );
  const [marketer, setMarketer] = useState(
    user?.accesses?.includes('marketer') || false,
  );
  const [transit, setTransit] = useState(
    user?.accesses?.includes('transit') || false,
  );

  const [financier, setFinancier] = useState(
    user?.accesses?.includes('financier') || false,
  );

  const [reader, setReader] = useState(
    user?.accesses?.includes('reader') || false,
  );

  const [fulfillment, setFulfillment] = useState(
    user?.accesses?.includes('fulfillment') || false,
  );

  const [warehouseRussia, setWarehouseRussia] = useState(
    user?.accesses?.includes('warehouseRussia') || false,
  );

  const [customsDeliveryManager, setCustomsDeliveryManager] = useState(
    user?.accesses?.includes('customsDeliveryManager') || false,
  );

  const [declarant, setDeclarant] = useState(
    user?.accesses?.includes('declarant') || false,
  );

  const [supplier, setSupplier] = useState(user?.supplier?.id);

  const [limit, setLimit] = useState(user?.purchaseLimit || null);

  const [writeOffLimit, setWriteOffLimit] = useState(
    user?.autoWriteOffLimit || 200,
  );

  const [notificationStates, setNotificationStates] =
    useState<NotificationStates>({
      promoEmail: true,
      serviceEmail: true,
      serviceSms: true,
      serviceTelegram: true,
      serviceMax: true,
      orderChatEmail: true,
      orderChatTelegram: true,
      orderChatMax: true,
      financeTelegram: true,
      financeMax: true,
      securityTelegram: true,
      securityMax: true,
    });

  const updateNotificationState = (
    key: keyof NotificationStates,
    value: boolean,
  ) => {
    setNotificationStates((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (user?.notifications) {
      updateNotificationState(
        'promoEmail',
        getNotificationState(user.notifications, 'promo', 'email'),
      );
      updateNotificationState(
        'serviceSms',
        getNotificationState(user.notifications, 'service', 'sms'),
      );
      updateNotificationState(
        'serviceEmail',
        getNotificationState(user.notifications, 'service', 'email'),
      );
      updateNotificationState(
        'serviceTelegram',
        getNotificationState(user.notifications, 'service', 'telegram'),
      );
      updateNotificationState(
        'serviceMax',
        getNotificationState(user.notifications, 'service', 'max'),
      );
      updateNotificationState(
        'orderChatEmail',
        getNotificationState(user.notifications, 'order_chat', 'email'),
      );
      updateNotificationState(
        'orderChatTelegram',
        getNotificationState(user.notifications, 'order_chat', 'telegram'),
      );
      updateNotificationState(
        'orderChatMax',
        getNotificationState(user.notifications, 'order_chat', 'max'),
      );
      updateNotificationState(
        'financeTelegram',
        getNotificationState(user.notifications, 'finance', 'telegram'),
      );
      updateNotificationState(
        'financeMax',
        getNotificationState(user.notifications, 'finance', 'max'),
      );
      updateNotificationState(
        'securityTelegram',
        getNotificationState(user.notifications, 'security', 'telegram'),
      );
      updateNotificationState(
        'securityMax',
        getNotificationState(user.notifications, 'security', 'max'),
      );
    }
  }, [user]);

  const [supplierClient, setSupplierClient] = useState(
    user?.defaultSupplierId || null,
  );

  const { hasPermission } = useRbac();
  const hasFullWritePermission = hasPermission(RbacObject.User, 'write:admin');

  function saveUser() {
    const userObj = {
      name,
      phone: phone || undefined,
      email,
      role,
      password,
      accesses: [] as Array<string>,
      disableEmails,
      supplierId: supplier,
      autoSend,
      moderate,
      moderateDeliveryOrder,
      purchaseLimit: limit,
      autoWriteOffLimit: writeOffLimit,
      defaultSupplierId: supplierClient,
      payBonusesForPurchaseOrder,
      notifications: [
        {
          type: 'service',
          sms: notificationStates.serviceSms,
          email: notificationStates.serviceEmail,
          telegram: notificationStates.serviceTelegram,
          max: notificationStates.serviceMax,
        },
        {
          type: 'promo',
          email: notificationStates.promoEmail,
        },
        {
          type: 'order_chat',
          email: notificationStates.orderChatEmail,
          telegram: notificationStates.orderChatTelegram,
          max: notificationStates.orderChatMax,
        },
        {
          type: 'finance',
          telegram: notificationStates.financeTelegram,
          max: notificationStates.financeMax,
        },
        {
          type: 'security',
          telegram: notificationStates.securityTelegram,
          max: notificationStates.securityMax,
        },
      ],
      paymentMethodsModerationEnabled,
      bitrixId: bitrixId || undefined,
    };

    if (userObj.role === 'employee') {
      if (purchase) {
        userObj.accesses.push('purchase');
      }
      if (warehouse) {
        userObj.accesses.push('warehouse');
      }
      if (salesManager) {
        userObj.accesses.push('salesManager');
      }
      if (supplierFinances) {
        userObj.accesses.push('supplierFinances');
      }
      if (marketer) {
        userObj.accesses.push('marketer');
      }
      if (transit) {
        userObj.accesses.push('transit');
      }
      if (financier) {
        userObj.accesses.push('financier');
      }
      if (reader) {
        userObj.accesses.push('reader');
      }
      if (fulfillment) {
        userObj.accesses.push('fulfillment');
      }

      if (warehouseRussia) {
        userObj.accesses.push('warehouseRussia');
      }
      if (customsDeliveryManager) {
        userObj.accesses.push('customsDeliveryManager');
      }
      if (declarant) {
        userObj.accesses.push('declarant');
      }
      // todo: проверять что выбрана хотя бы одна роль
    }

    const promise = isEdit
      ? UserService.editUser(user.id, userObj)
      : UserService.createUser(userObj);

    promise.then((data: { data: CleanUser }) => {
      AlertService.success();
      close();
      mutate();

      if (data.data.bitrixLinkCode && !isEdit) {
        ModalsService.createModal(BitrixMessageUserModal, {
          bitrixLinkCode: data.data.bitrixLinkCode,
        });
      }
    });
  }

  const modalRef = useModalClose(close);

  return (
    <div className="modal-dialog" ref={modalRef}>
      <div className="modal-content">
        <div className="modal-body">
          <div className="d-flex flex-row justify-content-between align-items-center">
            <h3>{isEdit ? 'Редактировать' : 'Добавить'} пользователя</h3>
            <div className="btn p-0" onClick={close}>
              <i className="bi bi-x fs-36 color-gray-600" />
            </div>
          </div>
          <Select
            title="Роль"
            className="mb-3"
            options={ROLES}
            value={role}
            placeholder=" "
            required
            onChange={({ target: { value } }) => setRole(value)}
            disabled={!hasFullWritePermission}
          />
          <div className="d-flex items-center mb-3 gap-2">
            <Toggle
              checked={isBitrix}
              disabled={isEdit}
              onChange={() => {
                setIsBitrix((prevState) => !prevState);
              }}
            />
            <div className="ml-2">Bitrix ID</div>
          </div>

          {isBitrix && (
            <>
              <div className={'d-flex gap-2'}>
                <div>ID контакта из Bitrix 24</div>
                <Whisper
                  placement="right"
                  trigger="hover"
                  speaker={
                    <Tooltip>
                      Используйте для синхронизации ID Bitrix 24 с ID клиента из
                      системы.
                      <br /> Узнать ID клиента в Bitrix24 можно, открыв контакт
                      клиента.
                      <br /> ID будет в адресной ссылке. Вставьте его в поле ID
                      контакта из Bitrix24
                    </Tooltip>
                  }
                >
                  <i className="ms-2 bi bi-question-circle" />
                </Whisper>
              </div>
              <Input
                className="mb-3"
                value={bitrixId}
                required
                onChange={({ target: { value } }) => setBitrixId(value)}
                disabled={!hasFullWritePermission || isEdit}
              />
            </>
          )}
          {role === 'employee' && (
            <div className="mb-3">
              <div className="mb-2">
                Права <span className="color-red">*</span>
              </div>
              <div className="d-flex flex-row flex-wrap justify-content-between">
                <div>
                  <Checkbox
                    title="Закупка"
                    className=""
                    checked={purchase}
                    onChange={({ target: { checked } }) => setPurchase(checked)}
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Менеджер по продажам"
                    checked={salesManager}
                    onChange={({ target: { checked } }) =>
                      setSalesManager(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Маркетолог"
                    checked={marketer}
                    onChange={({ target: { checked } }) => setMarketer(checked)}
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Читатель"
                    checked={reader}
                    onChange={({ target: { checked } }) => setReader(checked)}
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Финансист"
                    checked={financier}
                    onChange={({ target: { checked } }) =>
                      setFinancier(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                </div>
                <div>
                  <Checkbox
                    title="Склад"
                    className=""
                    checked={warehouse}
                    onChange={({ target: { checked } }) =>
                      setWarehouse(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Склад: транзит"
                    checked={transit}
                    onChange={({ target: { checked } }) => setTransit(checked)}
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Склад: фулфилмент"
                    checked={fulfillment}
                    onChange={({ target: { checked } }) =>
                      setFulfillment(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Поставщик: финансы"
                    checked={supplierFinances}
                    onChange={({ target: { checked } }) =>
                      setSupplierFinances(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Склад  РФ"
                    checked={warehouseRussia}
                    onChange={({ target: { checked } }) =>
                      setWarehouseRussia(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Менеджер  ВЭД"
                    checked={customsDeliveryManager}
                    onChange={({ target: { checked } }) =>
                      setCustomsDeliveryManager(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                  <Checkbox
                    title="Декларант"
                    checked={declarant}
                    onChange={({ target: { checked } }) =>
                      setDeclarant(checked)
                    }
                    disabled={!hasFullWritePermission}
                  />
                </div>
              </div>
            </div>
          )}
          <Input
            title="Имя"
            className="mb-3"
            value={name}
            required
            onChange={({ target: { value } }) => setName(value)}
            disabled={!hasFullWritePermission}
          />

          <PhoneInput
            title="Номер телефона"
            className="mb-3"
            value={formatPhoneNumber(phone)}
            onChange={setPhone}
            disabled={!hasFullWritePermission}
          />
          <Input
            title="Email"
            className="mb-3"
            onChange={({ target: { value } }) => setEmail(value)}
            value={email}
            type="email"
            disabled={!hasFullWritePermission}
          />
          {role && role !== 'client' && (
            <Checkbox
              title="Отключить email уведомления"
              className="me-3"
              checked={disableEmails}
              onChange={({ target: { checked } }) => setDisableEmails(checked)}
              disabled={!hasFullWritePermission}
            />
          )}
          <div className={' position-relative'}>
            <Input
              title="Пароль"
              className="mb-3"
              onChange={({ target: { value } }) => setPassword(value)}
              value={password}
              type="password"
              disabled={!hasFullWritePermission}
            />
            {role === 'client' && (
              <div
                className={'position-absolute top-0 end-0 ms-2 cursor-pointer'}
                onClick={() => {
                  setPassword(generatePassword());
                }}
              >
                <i className={'bi bi-shuffle'} />
              </div>
            )}
          </div>

          {role && role === 'client' && (
            <Select
              title="Привязанный поставщик"
              placeholder="Не выбрано"
              className="mb-3"
              options={
                suppliers?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                })) ?? []
              }
              withEmpty
              value={supplierClient}
              onChange={({ target: { value } }) => setSupplierClient(value)}
            />
          )}

          <div className="flex d-flex">
            <div className="flex-column">
              {role === 'client' ? (
                <div className="d-flex mb-3">
                  <Toggle
                    checked={autoSend}
                    onChange={(val) => setAutoSend(val)}
                  />
                  <div className="ms-2">Авто-отправка</div>
                </div>
              ) : null}
              {role === 'client' ? (
                <div className="d-flex mb-3">
                  <Toggle
                    checked={moderate}
                    onChange={(val) => setModerate(val)}
                  />
                  <div className="ms-2">Модерация выкупов</div>
                </div>
              ) : null}
              {role === 'client' ? (
                <div className="d-flex mb-3">
                  <Toggle
                    checked={moderateDeliveryOrder}
                    onChange={(val) => setModerateDeliveryOrder(val)}
                  />
                  <div className="ms-2">Модерация доставок</div>
                </div>
              ) : null}
              {role === 'client' ? (
                <div className="d-flex mb-3">
                  <Toggle
                    checked={!!limit}
                    onChange={(val) => setLimit(val ? '100' : null)}
                  />
                  <div className="ms-2">Лимит заказа</div>
                </div>
              ) : null}
            </div>
          </div>
          {role === 'client' && limit ? (
            <div className="d-flex flex-row mb-3 justify-content-between">
              {['50', '100', '500', '1000'].map((value) => (
                <button
                  onClick={() => setLimit(value)}
                  className={classNames(
                    limit === value ? 'btn-success' : 'btn-light',
                    'btn',
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
          ) : null}
          {role === 'client' ? (
            <div className="d-flex mb-3">
              <Toggle
                checked={paymentMethodsModerationEnabled}
                onChange={(val) => setPaymentMethodsModerationEnabled(val)}
              />
              <div className="ms-2">Модерация платёжных способов</div>
            </div>
          ) : null}

          {role === 'client' ? (
            <div className="d-flex mb-3">
              <Toggle
                checked={payBonusesForPurchaseOrder}
                onChange={(val) => setPayBonusesForPurchaseOrder(val)}
              />
              <div className="ms-2">Оплата бонусами выкупов</div>
            </div>
          ) : null}
          {role === 'client' ? (
            <Input
              title="Лимит автосписания"
              className="mb-3"
              onChange={({ target: { value } }) => setWriteOffLimit(value)}
              value={writeOffLimit}
              type="number"
            />
          ) : null}
          {role === 'client' ? (
            <div className="d-flex mb-3">
              <NotificationTable
                notificationStates={notificationStates}
                updateNotificationState={updateNotificationState}
                telegramMailing={user?.telegramMailing}
                maxMailing={user?.maxMailing}
              />
            </div>
          ) : null}
          {role === 'employee' ? (
            <Select
              title="Поставщик"
              placeholder="Выберите поставщика"
              className="mb-3"
              options={
                suppliers?.map(({ id, name }) => ({
                  value: id,
                  label: name,
                })) ?? []
              }
              withEmpty
              value={supplier}
              onChange={({ target: { value } }) => setSupplier(value)}
              disabled={!hasFullWritePermission}
            />
          ) : null}
        </div>
        <div className="modal-footer">
          <div className="d-flex flex-row justify-content-end">
            <div className="btn btn-success" onClick={saveUser}>
              {isEdit ? 'Сохранить' : 'Добавить'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
